import React from 'react';
import { View, TouchableOpacity, Text, ScrollView } from 'react-native';
import { X } from 'lucide-react-native';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { ExploreFilters, DEFAULT_FILTERS } from '@/hooks/useExploreFilters';
import { useTranslation } from 'react-i18next';

interface FilterPillRowProps {
  filters: ExploreFilters;
  onRemoveFilter: (key: keyof ExploreFilters, value?: any) => void;
  onClearAll: () => void;
}

// Human-readable label keys for filter values
const DIFFICULTY_LABEL_KEYS: Record<string, string> = { easy: 'filters.easy', moderate: 'filters.moderate', hard: 'filters.hard' };
const SORT_LABEL_KEYS: Record<string, string> = { match_score: 'filters.bestMatch', distance: 'filters.closest', newest: 'filters.newest', rating: 'filters.topRated' };
const DISTANCE_RANGE_KEYS: Record<string, string> = {
  '0-1000': 'filters.under1km',
  '1000-5000': 'filters.oneTo5km',
  '5000-15000': 'filters.fiveTo15km',
  '15000-35000': 'filters.fifteenTo35km',
};
const LENGTH_RANGE_KEYS: Record<string, string> = {
  '0-2000': 'filters.short',
  '2000-5000': 'filters.medium',
  '5000-null': 'filters.long',
};
const RATING_LABELS: Record<string, string> = { '3': '3+ ★', '4': '4+ ★', '4.5': '4.5+ ★' };

interface PillData {
  key: string;
  label: string;
  filterKey: keyof ExploreFilters;
  value?: any;
}

export default function FilterPillRow({ filters, onRemoveFilter, onClearAll }: FilterPillRowProps) {
  const { t } = useTranslation();
  const pills: PillData[] = [];

  // Sort
  if (filters.sort !== DEFAULT_FILTERS.sort) {
    pills.push({ key: 'sort', label: t(SORT_LABEL_KEYS[filters.sort] || filters.sort), filterKey: 'sort', value: DEFAULT_FILTERS.sort });
  }

  // Tags
  filters.tags.forEach((tag) => {
    pills.push({ key: `tag-${tag}`, label: tag, filterKey: 'tags', value: tag });
  });

  // Difficulty
  if (filters.difficulty) {
    pills.push({ key: 'difficulty', label: t(DIFFICULTY_LABEL_KEYS[filters.difficulty] || filters.difficulty), filterKey: 'difficulty', value: null });
  }

  // Trail Length range (min/maxDistance = trail length)
  if (filters.minDistance !== null || filters.maxDistance !== null) {
    const rangeKey = `${filters.minDistance}-${filters.maxDistance}`;
    const lengthLabel = LENGTH_RANGE_KEYS[rangeKey];
    const label = lengthLabel ? t(lengthLabel) : `${((filters.minDistance || 0) / 1000).toFixed(0)}–${filters.maxDistance ? ((filters.maxDistance) / 1000).toFixed(0) + ' km' : '+'}`;
    pills.push({ key: 'length', label, filterKey: 'minDistance', value: null });
  }

  // Distance From You range
  if (filters.minUserDistance !== null || filters.maxUserDistance !== null) {
    const rangeKey = `${filters.minUserDistance}-${filters.maxUserDistance}`;
    const distanceLabel = DISTANCE_RANGE_KEYS[rangeKey];
    const label = distanceLabel ? t(distanceLabel) : `${((filters.minUserDistance || 0) / 1000).toFixed(0)}–${filters.maxUserDistance ? ((filters.maxUserDistance) / 1000).toFixed(0) + ' km' : '+'}`;
    pills.push({ key: 'userDistance', label, filterKey: 'minUserDistance', value: null });
  }

  // Dog traits
  if (filters.offLeash) pills.push({ key: 'offLeash', label: t('filters.offLeashFriendly'), filterKey: 'offLeash', value: null });
  if (filters.wildlife) pills.push({ key: 'wildlife', label: t('filters.wildlifePresent'), filterKey: 'wildlife', value: null });
  if (filters.lowDogTraffic) pills.push({ key: 'lowDogTraffic', label: t('filters.lowDogTraffic'), filterKey: 'lowDogTraffic', value: null });
  if (filters.lowFootTraffic) pills.push({ key: 'lowFootTraffic', label: t('filters.lowFootTraffic'), filterKey: 'lowFootTraffic', value: null });
  if (filters.lowExposure) pills.push({ key: 'lowExposure', label: t('filters.shadedLowExposure'), filterKey: 'lowExposure', value: null });

  // Rating
  if (filters.minRating !== null) {
    pills.push({ key: 'rating', label: RATING_LABELS[String(filters.minRating)] || `${filters.minRating}+ ★`, filterKey: 'minRating', value: null });
  }

  // Location
  if (filters.searchLocationName) {
    pills.push({ key: 'location', label: `📍 ${filters.searchLocationName}`, filterKey: 'searchLocationName', value: null });
  }

  if (pills.length === 0) return null;

  return (
    <View style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        {pills.map((pill) => (
          <TouchableOpacity
            key={pill.key}
            style={styles.pill}
            onPress={() => onRemoveFilter(pill.filterKey, pill.value)}
            activeOpacity={0.7}
          >
            <Text style={styles.pillText}>{pill.label}</Text>
            <X size={14} color={theme.accentPrimary} strokeWidth={2.5} />
          </TouchableOpacity>
        ))}
        <TouchableOpacity style={styles.clearAll} onPress={onClearAll} activeOpacity={0.7}>
          <Text style={styles.clearAllText}>Clear all</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
}

const styles = {
  container: {
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  scrollContent: {
    gap: 8,
    paddingVertical: 4,
  },
  pill: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 254, 119, 0.15)',
    borderWidth: 1,
    borderColor: theme.accentPrimary,
  },
  pillText: {
    ...Typography.caption(theme.accentPrimary),
    fontWeight: '600' as const,
  },
  clearAll: {
    justifyContent: 'center' as const,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  clearAllText: {
    ...Typography.caption(theme.textMuted),
    textDecorationLine: 'underline' as const,
  },
};